import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { Issue, IssueStatus, IssuePriority } from '@/types'
import * as issueApi from '../../services/api/issue'
import { updateIssueLabels } from './labelSlice'

interface IssueState {
  issues: Issue[]
  currentIssue: Issue | null
  loading: boolean
  error: string | null
}

const initialState: IssueState = {
  issues: [],
  currentIssue: null,
  loading: false,
  error: null,
}

export const fetchIssues = createAsyncThunk(
  'issue/fetchIssues',
  async (projectKey: string) => {
    const response = await issueApi.getIssues(projectKey)
    return response.data
  }
)

export const createIssue = createAsyncThunk(
  'issue/createIssue',
  async (issueData: {
    title: string
    description?: string
    projectId: string
    assigneeId?: string
    priority: IssuePriority
    dueDate?: string
    customStatus?: string
  }) => {
    const response = await issueApi.createIssue(issueData)
    return response.data
  }
)

export const updateIssueStatus = createAsyncThunk(
  'issue/updateIssueStatus',
  async ({ issueId, status }: { issueId: string; status: IssueStatus }) => {
    const response = await issueApi.updateIssue(issueId, { status })
    return response.data
  }
)

export const updateIssue = createAsyncThunk(
  'issue/updateIssue',
  async ({ issueId, updateData }: {
    issueId: string;
    updateData: Partial<{
      title: string
      description: string
      status: IssueStatus
      priority: IssuePriority
      assigneeId: string | null
      position: number
      dueDate: string | null
    }>
  }) => {
    const response = await issueApi.updateIssue(issueId, updateData)
    return response.data
  }
)

export const updateIssueCustomField = createAsyncThunk(
  'issue/updateIssueCustomField',
  async ({ issueId, fieldId, value }: {
    issueId: string;
    fieldId: string;
    value: string | number | null
  }) => {
    // 這裡我們需要導入 customField API
    const { updateIssueCustomFieldValue } = await import('../../services/api/customField')
    const updatedCustomFieldValue = await updateIssueCustomFieldValue(issueId, fieldId, value)

    // 返回更新的完整數據
    return { issueId, customFieldValue: updatedCustomFieldValue }
  }
)

export const deleteIssue = createAsyncThunk(
  'issue/deleteIssue',
  async (issueId: string) => {
    await issueApi.deleteIssue(issueId)
    return issueId
  }
)

export const archiveIssue = createAsyncThunk(
  'issue/archiveIssue',
  async ({ issueId, archived }: { issueId: string; archived: boolean }) => {
    const response = await issueApi.archiveIssue(issueId, archived)
    return response.data
  }
)

export const moveIssue = createAsyncThunk(
  'issue/moveIssue',
  async ({ issueId, targetProjectId }: { issueId: string; targetProjectId: string }) => {
    const response = await issueApi.moveIssue(issueId, targetProjectId)
    return response.data
  }
)

const issueSlice = createSlice({
  name: 'issue',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentIssue: (state, action) => {
      state.currentIssue = action.payload
    },
    updateIssueInList: (state, action) => {
      const index = state.issues.findIndex(issue => issue.id === action.payload.id)
      if (index !== -1) {
        state.issues[index] = action.payload
      }
    },
    addIssueToList: (state, action) => {
      // 檢查議題是否已存在，避免重複添加
      const existingIndex = state.issues.findIndex(issue => issue.id === action.payload.id)
      if (existingIndex === -1) {
        state.issues.push(action.payload)
      }
    },
    removeIssueFromList: (state, action) => {
      state.issues = state.issues.filter(issue => issue.id !== action.payload)
    },
    optimisticUpdateIssueStatus: (state, action) => {
      const { issueId, status, customStatus, position } = action.payload
      const index = state.issues.findIndex(issue => issue.id === issueId)
      if (index !== -1) {
        if (status !== undefined) {
          state.issues[index].status = status
        }
        if (customStatus !== undefined) {
          state.issues[index].customStatus = customStatus
        }
        if (position !== undefined) {
          state.issues[index].position = position
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch issues
      .addCase(fetchIssues.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchIssues.fulfilled, (state, action) => {
        state.loading = false
        const fetchedIssues = action.payload?.issues || []
        
        // 調試：檢查從後端載入的議題是否包含位置信息
        console.log('從後端載入的議題:', fetchedIssues.map(issue => ({
          id: issue.id,
          title: issue.title,
          status: issue.status,
          position: issue.position
        })))
        
        state.issues = fetchedIssues
      })
      .addCase(fetchIssues.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || '載入議題失敗'
      })
      // Create issue
      .addCase(createIssue.fulfilled, (state, action) => {
        if (action.payload?.issue) {
          // 檢查議題是否已存在，避免重複添加
          const existingIndex = state.issues.findIndex(issue => issue.id === action.payload!.issue.id)
          if (existingIndex === -1) {
            state.issues.push(action.payload.issue)
          }
        }
      })
      // Update issue status
      .addCase(updateIssueStatus.fulfilled, (state, action) => {
        if (action.payload?.issue) {
          const index = state.issues.findIndex(issue => issue.id === action.payload!.issue.id)
          if (index !== -1) {
            state.issues[index] = action.payload.issue
          }
        }
      })
      // Update issue
      .addCase(updateIssue.fulfilled, (state, action) => {
        if (action.payload?.issue) {
          const updatedIssue = action.payload.issue
          const index = state.issues.findIndex(issue => issue.id === updatedIssue.id)
          if (index !== -1) {
            console.log('Redux: 更新議題成功', {
              id: updatedIssue.id,
              title: updatedIssue.title,
              status: updatedIssue.status,
              position: updatedIssue.position,
              oldPosition: state.issues[index].position
            })
            state.issues[index] = updatedIssue
          }
        }
      })
      // Delete issue
      .addCase(deleteIssue.fulfilled, (state, action) => {
        state.issues = state.issues.filter(issue => issue.id !== action.payload)
      })
      // Archive issue
      .addCase(archiveIssue.fulfilled, (state, action) => {
        if (action.payload?.issue) {
          const index = state.issues.findIndex(issue => issue.id === action.payload!.issue.id)
          if (index !== -1) {
            state.issues[index] = action.payload.issue
          }
        }
      })
      // Update issue labels
      .addCase(updateIssueLabels.fulfilled, (state, action) => {
        if (action.payload?.issue) {
          const index = state.issues.findIndex(issue => issue.id === action.payload!.issue.id)
          if (index !== -1) {
            state.issues[index] = action.payload.issue
          }
          // 如果是當前議題，也更新 currentIssue
          if (state.currentIssue && state.currentIssue.id === action.payload.issue.id) {
            state.currentIssue = action.payload.issue
          }
        }
      })
      // Move issue
      .addCase(moveIssue.fulfilled, (state, action) => {
        if (action.payload?.issue) {
          // 從當前專案的議題列表中移除
          state.issues = state.issues.filter(issue => issue.id !== action.payload!.issue.id)
          // 如果是當前議題，清空 currentIssue
          if (state.currentIssue && state.currentIssue.id === action.payload.issue.id) {
            state.currentIssue = null
          }
        }
      })
      // Update issue custom field
      .addCase(updateIssueCustomField.fulfilled, (state, action) => {
        const { issueId, customFieldValue } = action.payload

        // 更新 currentIssue 的自訂欄位值
        if (state.currentIssue && state.currentIssue.id === issueId) {
          if (!state.currentIssue.customFieldValues) {
            state.currentIssue.customFieldValues = []
          }

          const existingFieldIndex = state.currentIssue.customFieldValues.findIndex(
            cfv => cfv.fieldId === customFieldValue.fieldId
          )

          if (existingFieldIndex !== -1) {
            // 更新現有欄位值
            state.currentIssue.customFieldValues[existingFieldIndex] = customFieldValue
          } else {
            // 添加新的欄位值
            state.currentIssue.customFieldValues.push(customFieldValue)
          }
        }

        // 也更新 issues 列表中的對應議題
        const issueIndex = state.issues.findIndex(issue => issue.id === issueId)
        if (issueIndex !== -1) {
          const issue = state.issues[issueIndex]
          if (!issue.customFieldValues) {
            issue.customFieldValues = []
          }

          const existingFieldIndex = issue.customFieldValues.findIndex(
            cfv => cfv.fieldId === customFieldValue.fieldId
          )

          if (existingFieldIndex !== -1) {
            // 更新現有欄位值
            issue.customFieldValues[existingFieldIndex] = customFieldValue
          } else {
            // 添加新的欄位值
            issue.customFieldValues.push(customFieldValue)
          }
        }
      })
  },
})

export const { clearError, setCurrentIssue, updateIssueInList, addIssueToList, removeIssueFromList, optimisticUpdateIssueStatus } = issueSlice.actions
export default issueSlice.reducer